import { ArrowUpRight, ClipboardCheck, Lightbulb, Radar, Rocket, Scale, Workflow } from "lucide-react";
import { useLang, IMG_CIRCUIT, IMG_SCREEN } from "../i18n";
import { scrollToId, trackEvent } from "../lib";
import { Reveal, Section, SectionHead } from "./Reveal";

const ICONS = {
  lightbulb: Lightbulb,
  scale: Scale,
  workflow: Workflow,
  check: ClipboardCheck,
  rocket: Rocket,
  radar: Radar,
  arrow: ArrowUpRight,
};

const SPANS = {
  ideacao: "md:col-span-8 md:row-span-2",
  governanca: "md:col-span-4",
  sdlc: "md:col-span-4 md:row-span-2",
  entregas: "md:col-span-4",
  rollout: "md:col-span-4",
  obs: "md:col-span-8",
  diagnostico: "md:col-span-4",
};

const IMAGES = { ideacao: IMG_CIRCUIT, sdlc: IMG_SCREEN };

export const Lifecycle = () => {
  const { t } = useLang();

  return (
    <Section id="ciclo">
      <SectionHead label={t.ciclo.label} title={t.ciclo.heading} sub={t.ciclo.sub} />
      <div className="grid grid-cols-1 gap-5 md:auto-rows-[minmax(170px,auto)] md:grid-cols-8 lg:gap-6">
        {t.ciclo.cards.map((c, i) => {
          const Icon = ICONS[c.icon] || Radar;
          const img = IMAGES[c.key];
          const isDiag = c.key === "diagnostico";
          const inner = (
            <div
              data-testid={`lifecycle-card-${c.key}`}
              className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border p-7 transition-all duration-300 md:p-8 ${
                isDiag
                  ? "border-volt/40 bg-ink-900 hover:bg-ink-800"
                  : "border-line bg-ink-900/60 hover:border-volt/30 hover:bg-ink-800"
              }`}
            >
              {img && (
                <img
                  src={img}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale transition-all duration-500 group-hover:scale-105 group-hover:opacity-35"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/20" />
              <div className="relative flex h-full flex-col">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.25em] text-volt">{c.id}</span>
                  <Icon
                    size={20}
                    className="text-white/50 transition-colors group-hover:text-volt"
                  />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{c.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-mist">{c.desc}</p>
                {c.steps && (
                  <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-5">
                    {c.steps.map((s, si) => (
                      <li
                        key={s}
                        data-testid={`lifecycle-step-${si + 1}`}
                        className="flex items-baseline gap-3"
                      >
                        <span className="font-mono text-[10px] tracking-widest text-volt/80">
                          {String(si + 1).padStart(2, "0")}
                        </span>
                        <span className="text-xs leading-relaxed text-white/70 sm:text-[13px]">{s}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {isDiag && (
                  <span className="mt-auto pt-6 font-mono text-xs uppercase tracking-[0.2em] text-volt">
                    {t.ciclo.diagCta}
                  </span>
                )}
              </div>
            </div>
          );
          return (
            <Reveal key={c.key} delay={(i % 4) * 0.06} className={SPANS[c.key]}>
              {isDiag ? (
                <a
                  href="#contato"
                  onClick={(e) => {
                    e.preventDefault();
                    trackEvent("diagnostic_click", { section: "ciclo" });
                    scrollToId("#contato");
                  }}
                  className="block h-full"
                >
                  {inner}
                </a>
              ) : (
                inner
              )}
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
};
